import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formUrl = 'https://readdy.ai/api/form/d9qmdosnlsngrm4lm230';

  useEffect(() => {
    document.title = '关于我们 - 饭岛爱纪念网';

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', '饭岛爱纪念网致力于保存和传播日本传奇女性饭岛爱的文化影响力。了解我们的使命、品牌声誉以及联系方式。');
    setMeta('keywords', '饭岛爱纪念网,关于我们,饭岛爱,品牌介绍,联系方式');
    setMeta('og:title', '关于我们 - 饭岛爱纪念网', true);
    setMeta('og:type', 'website', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://iijima-ai-memorial.com/about');

    const existingScript = document.getElementById('about-jsonld');
    if (existingScript) existingScript.remove();

    const jsonLd = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '饭岛爱纪念网',
        url: 'https://iijima-ai-memorial.com',
        description: '致力于保存和传播日本传奇女性饭岛爱的文化影响力',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@iijima-ai-memorial.com',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '关于我们',
        description: '饭岛爱纪念网关于我们页面',
        url: 'https://iijima-ai-memorial.com/about',
      },
    ];

    const script = document.createElement('script');
    script.id = 'about-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);


  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpValue = (formData.get('contact_alt') as string || '').trim();
    if (hpValue) {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('contact_alt');

    if (!formUrl) {
      setFormError('表单暂不可用，请稍后再试');
      setFormStatus('error');
      return;
    }

    try {
      const resp = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 min-h-screen">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap" aria-label="breadcrumb">
              <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">关于我们</h1>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <section className="mb-14">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-5">品牌介绍</h2>
                <p className="text-foreground-700 leading-loose mb-4">
                  饭岛爱纪念网成立于2024年，是一个致力于保存和传播日本传奇女性饭岛爱文化影响力的非商业纪念网站。
                </p>
                <p className="text-foreground-700 leading-loose mb-4">
                  饭岛爱（1972-2008）是日本娱乐界最具传奇色彩的女性之一。她的人生轨迹——从普通女孩到AV女优，再到畅销作家和电视主持人——不仅是个人的奋斗史，更是日本社会变迁的缩影。她用真实的生命诠释了勇气的含义，她的自传《柏拉图式性爱》改变了无数人对女性、对社会的看法。
                </p>
                <p className="text-foreground-700 leading-loose mb-4">
                  我们通过收集、整理和展示饭岛爱的生平经历、代表作品、社会影响以及来自世界各地的纪念文章，希望让更多人了解这位非凡女性的人生故事。她的影响力超越了时间和国界，持续启发着新一代的读者和思考者。
                </p>
                <p className="text-foreground-700 leading-loose">
                  本站所有内容均为编辑团队基于公开资料整理编写，如有不准确之处，欢迎联系我们指正。
                </p>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-5">我们的使命</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="bg-background-100 rounded-lg p-5 border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                      <i className="ri-book-read-line text-primary-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">知识保存</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      系统性地整理和保存饭岛爱的生平资料、作品和媒体报道，确保这些宝贵的文化遗产不被时间遗忘。
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-5 border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-3">
                      <i className="ri-global-line text-accent-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">文化传播</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      向中文世界介绍饭岛爱的故事，让更多人了解她对日本社会、女性权益和文化的深远影响。
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-5 border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 mb-3">
                      <i className="ri-chat-heart-line text-secondary-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">启发思考</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      通过饭岛爱的人生故事，引发关于女性权益、心理健康、社会包容等议题的深入讨论和思考。
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-5 border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                      <i className="ri-community-line text-primary-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">社区连接</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      为饭岛爱的粉丝和支持者提供一个交流与纪念的平台，凝聚来自世界各地的敬意与追思。
                    </p>
                  </div>
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-5">品牌声誉</h2>
                <div className="bg-background-100 rounded-lg p-6 border border-background-200">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                    <div>
                      <div className="text-3xl font-heading font-bold text-accent-500 mb-1">50+</div>
                      <p className="text-sm text-foreground-500">精选文章</p>
                    </div>
                    <div>
                      <div className="text-3xl font-heading font-bold text-primary-500 mb-1">5</div>
                      <p className="text-sm text-foreground-500">内容分类</p>
                    </div>
                    <div>
                      <div className="text-3xl font-heading font-bold text-secondary-500 mb-1">10万+</div>
                      <p className="text-sm text-foreground-500">月访问量</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-accent-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-900 text-sm mb-1">信息准确性</h4>
                      <p className="text-foreground-500 text-sm leading-relaxed">所有内容基于公开资料和可靠来源编写，经过编辑团队严格审核</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0 mt-0.5">
                      <i className="ri-user-heart-line text-primary-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-900 text-sm mb-1">尊重与敬意</h4>
                      <p className="text-foreground-500 text-sm leading-relaxed">以最大的尊重对待饭岛爱及其家人，坚持正面、积极的报道视角</p>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <section className="bg-background-100 rounded-lg p-6 border border-background-200 mb-6">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5">联系我们</h2>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form=""
                    className="flex flex-col gap-4"
                  >
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="about-subject"
                        name="subject"
                        type="text"
                        className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（限500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-hp-field"
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm"
                    >
                      发送消息
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">消息已发送，感谢您的联系！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </section>

                <section className="bg-background-100 rounded-lg p-6 border border-background-200">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">其他联系方式</h3>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200">
                        <i className="ri-mail-line text-foreground-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-foreground-600">contact@iijima-ai-memorial.com</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200">
                        <i className="ri-twitter-x-line text-foreground-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-foreground-600">@iijima_memorial</span>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}